/**
 * Marketing → Appeal FE path mapping (M3 / S4)
 *
 * @see .cursor/plans/archived/2026-q2/marketing-appeal-consolidation/stream-s4-fe-relocation.md
 */

/** Legacy marketing subdomains → canonical appeal / entity list paths */
export const LEGACY_MARKETING_DOMAIN_LIST_PATHS = {
  marketing_campaign: '/appeal/campaign',
  conversion_funnel: '/entity/external',
  content_asset: '/appeal/content',
} as const;

export type LegacyMarketingDomain = keyof typeof LEGACY_MARKETING_DOMAIN_LIST_PATHS;

const CONTENT_ASSET_SEMANTIC_ID_PREFIX = 'mig_ca_sf_';

/** Bridge businessDomain + record id → canonical appeal/entity detail path (no /marketing/* hop). */
export function resolveLegacyMarketingDomainDetailPath(
  businessDomain: LegacyMarketingDomain,
  recordId: string
): string {
  switch (businessDomain) {
    case 'marketing_campaign':
      return `/appeal/ephemeral-performance/${recordId}`;
    case 'conversion_funnel':
      return `/entity/external/${recordId}`;
    case 'content_asset':
      return `/appeal/semantic-formation/${CONTENT_ASSET_SEMANTIC_ID_PREFIX}${recordId}`;
    default: {
      const exhaustive: never = businessDomain;
      return exhaustive;
    }
  }
}

export function isLegacyMarketingDomain(
  businessDomain: string
): businessDomain is LegacyMarketingDomain {
  return businessDomain in LEGACY_MARKETING_DOMAIN_LIST_PATHS;
}

/**
 * Map `/marketing/*` pathname (+ optional search) to appeal canonical path.
 * Preserves query string (e.g. relationStage filters).
 */
export function mapMarketingPathToAppeal(pathname: string, search = ''): string {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const segments = normalized.split('/').filter(Boolean);

  if (segments[0] !== 'marketing') {
    return `${normalized}${search}`;
  }

  const resource = segments[1];
  const recordId = segments[2];

  let target: string;
  switch (resource) {
    case undefined:
    case 'dashboard':
      target = '/appeal/dashboard';
      break;
    case 'campaign':
      target = recordId ? `/appeal/ephemeral-performance/${recordId}` : '/appeal/campaign';
      break;
    case 'funnel':
      target = recordId ? `/entity/external/${recordId}` : '/entity/external';
      break;
    case 'content-asset':
      target = recordId
        ? `/appeal/semantic-formation/${CONTENT_ASSET_SEMANTIC_ID_PREFIX}${recordId}`
        : '/appeal/content';
      break;
    default:
      target = '/appeal/dashboard';
      break;
  }

  return `${target}${search}`;
}

export function isLegacyMarketingPath(pathname: string): boolean {
  return pathname === '/marketing' || pathname.startsWith('/marketing/');
}
